import { useEffect, useState } from "react";
import { Campaigns as CampaignsAPI } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Envelope, ChatCircleText, Clock, Sparkle, TrendUp } from "@phosphor-icons/react";

export default function CampaignsPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [statsFor, setStatsFor] = useState(null);
  const [stats, setStats] = useState(null);

  const load = () => CampaignsAPI.list().then(setList);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete campaign?")) return;
    await CampaignsAPI.remove(id);
    load();
  };

  const generate = async (id) => {
    toast.info("Generating variants with Claude...");
    try {
      const r = await CampaignsAPI.generateVariants(id, {});
      toast.success(`${r.subject_variants.length} variants generated`);
      load();
    } catch { toast.error("Failed"); }
  };

  const openStats = async (c) => {
    setStatsFor(c);
    try {
      const r = await CampaignsAPI.variantStats(c.id);
      setStats(r);
    } catch { toast.error("No stats yet"); }
  };

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        overline="Sequences · Cadences"
        title="Campaigns"
        description="Design multi-step follow-up sequences. Attach to prospects to auto-queue follow-ups."
        testId="campaigns-header"
        actions={
          <Button onClick={() => setOpen(true)} className="bg-neutral-900 hover:bg-black text-white" data-testid="new-campaign-btn">
            <Plus size={16} className="mr-2" /> New Campaign
          </Button>
        }
      />

      <div className="p-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in" data-testid="campaigns-grid">
        {list.length === 0 && (
          <div className="col-span-full text-center py-20 text-sm text-neutral-500">
            No campaigns yet. Create your first outreach sequence.
          </div>
        )}
        {list.map(c => (
          <Card key={c.id} className="p-6 rounded-md border-neutral-200 hover:border-neutral-900 transition-colors" data-testid={`campaign-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {c.channel === "email" ? <Envelope size={14} weight="duotone" /> : <ChatCircleText size={14} weight="duotone" />}
                  <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-widest">{c.channel}</Badge>
                </div>
                <div className="font-display text-xl">{c.name}</div>
              </div>
              <div className="flex gap-1">
                {c.channel === "email" && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => generate(c.id)} data-testid={`variants-btn-${c.id}`} title="Generate A/B variants">
                      <Sparkle size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openStats(c)} data-testid={`stats-btn-${c.id}`} title="A/B stats">
                      <TrendUp size={14} />
                    </Button>
                  </>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`delete-campaign-${c.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
            {c.subject && <div className="text-xs text-neutral-500 mt-3 font-mono">RE: {c.subject}</div>}
            {c.auto_promoted_variant !== null && c.auto_promoted_variant !== undefined && (
              <Badge className="bg-emerald-600 text-white text-[10px] font-mono mt-1">AUTO-PROMOTED</Badge>
            )}
            {c.subject_variants?.length > 0 && (
              <div className="mt-3 space-y-1" data-testid={`variants-${c.id}`}>
                <div className="overline">A/B variants</div>
                {c.subject_variants.map((v, i) => (
                  <div key={i} className="text-xs font-mono text-neutral-600 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 border border-neutral-300 rounded-full text-[9px]">{String.fromCharCode(65 + i)}</span>
                    {v}
                  </div>
                ))}
              </div>
            )}
            <div className="text-sm mt-3 text-neutral-700 line-clamp-3">{c.body}</div>
            <div className="mt-4 pt-4 border-t border-neutral-100">
              <div className="overline mb-2 flex items-center gap-1"><Clock size={11} /> Follow-ups</div>
              <div className="flex flex-wrap gap-1">
                {(c.follow_ups || []).length === 0 && <span className="text-xs text-neutral-400">None</span>}
                {(c.follow_ups || []).map((f, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] font-mono">Day +{f.delay_days}</Badge>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <NewCampaignDialog open={open} onOpenChange={setOpen} onSaved={load} />

      <Dialog open={!!statsFor} onOpenChange={() => setStatsFor(null)}>
        <DialogContent className="max-w-lg" data-testid="stats-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">A/B Stats · {statsFor?.name}</DialogTitle>
            <DialogDescription>Reply rate per subject-line variant. Auto-promotes when a variant leads by 20+ points over 30+ sends.</DialogDescription>
          </DialogHeader>
          {stats?.auto_promoted && (
            <div className="p-3 rounded-md border border-emerald-300 bg-emerald-50 text-sm" data-testid="auto-promoted-banner">
              <div className="overline text-emerald-700">Auto-promoted winner</div>
              <div className="font-mono text-sm mt-1 text-emerald-900">"{stats.auto_promoted.subject}"</div>
              <div className="text-xs text-emerald-800 mt-1">This variant is now the campaign's default subject. Future messages use it.</div>
            </div>
          )}
          {(stats?.variants || []).length === 0 && <div className="py-8 text-center text-sm text-neutral-500">No variants yet. Click the sparkle to generate.</div>}
          <div className="space-y-3">
            {(stats?.variants || []).map((v, i) => {
              const isPromoted = stats?.auto_promoted?.variant_index === v.variant_index;
              const winner = (stats.variants || []).every(x => v.reply_rate >= x.reply_rate) && v.sent > 0;
              return (
                <Card key={i} className={`p-4 rounded-md ${isPromoted ? "border-emerald-500 bg-emerald-50" : winner ? "border-neutral-900" : "border-neutral-200"}`} data-testid={`stat-${i}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 border border-neutral-400 rounded-full text-xs font-mono">{String.fromCharCode(65 + i)}</span>
                        {isPromoted && <Badge className="bg-emerald-600 text-white text-[10px] font-mono">PROMOTED</Badge>}
                        {!isPromoted && winner && v.sent > 0 && <Badge className="bg-neutral-900 text-white text-[10px] font-mono">LEADING</Badge>}
                      </div>
                      <div className="font-mono text-sm mt-2">{v.subject}</div>
                    </div>
                    <div className="text-right">
                      <div className="metric-num text-3xl">{v.reply_rate}%</div>
                      <div className="text-[10px] font-mono text-neutral-500 uppercase tracking-widest mt-1">{v.replied} / {v.sent}</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewCampaignDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ name: "", channel: "email", subject: "", body: "", follow_ups: [], auto_promote_min_sends: 30, auto_promote_min_lead: 20 });
  const [fu, setFu] = useState({ delay_days: 3, body: "", subject: "" });

  const addFu = () => {
    if (!fu.body) return;
    setF({ ...f, follow_ups: [...f.follow_ups, fu] });
    setFu({ delay_days: 3, body: "", subject: "" });
  };
  const removeFu = (i) => setF({ ...f, follow_ups: f.follow_ups.filter((_, idx) => idx !== i) });

  const submit = async () => {
    if (!f.name || !f.body) return toast.error("Name & body required");
    await CampaignsAPI.create(f);
    toast.success("Campaign created");
    setF({ name: "", channel: "email", subject: "", body: "", follow_ups: [], auto_promote_min_sends: 30, auto_promote_min_lead: 20 });
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">New Campaign</DialogTitle>
          <DialogDescription>Set an initial message and follow-up cadence.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={f.name} onChange={e => setF({...f, name: e.target.value})} placeholder="Restaurant Q1 outreach" data-testid="campaign-name" /></div>
            <div>
              <Label>Channel</Label>
              <Select value={f.channel} onValueChange={v => setF({...f, channel: v})}>
                <SelectTrigger data-testid="campaign-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.channel === "email" && (
            <div><Label>Initial subject</Label><Input value={f.subject} onChange={e => setF({...f, subject: e.target.value})} data-testid="campaign-subject" /></div>
          )}
          <div><Label>Initial message body</Label><Textarea rows={4} value={f.body} onChange={e => setF({...f, body: e.target.value})} data-testid="campaign-body" /></div>

          <div className="pt-4 border-t border-neutral-200">
            <div className="overline mb-3">Follow-ups</div>            {f.follow_ups.map((x, i) => (
              <div key={i} className="flex items-start gap-2 p-3 border border-neutral-200 rounded mb-2" data-testid={`fu-item-${i}`}>
                <Badge variant="outline" className="font-mono text-[10px]">Day +{x.delay_days}</Badge>
                <div className="flex-1 text-xs text-neutral-600">{x.body}</div>
                <Button size="sm" variant="ghost" onClick={() => removeFu(i)}><Trash size={12} /></Button>
              </div>
            ))}
            <div className="grid grid-cols-4 gap-2 items-end mt-2">
              <div>
                <Label className="text-xs">Delay (days)</Label>
                <Input type="number" value={fu.delay_days} onChange={e => setFu({...fu, delay_days: Number(e.target.value)})} data-testid="fu-delay" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Follow-up message</Label>
                <Input value={fu.body} onChange={e => setFu({...fu, body: e.target.value})} placeholder="Just following up..." data-testid="fu-body" />
              </div>
              <Button variant="outline" onClick={addFu} data-testid="add-fu-btn">Add</Button>
            </div>
          </div>

          {f.channel === "email" && (
            <div className="pt-4 border-t border-neutral-200">
              <div className="overline mb-3">Winner auto-promote threshold</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Min sends per variant</Label>
                  <Input type="number" min={5} value={f.auto_promote_min_sends} onChange={e => setF({...f, auto_promote_min_sends: Number(e.target.value)})} data-testid="threshold-min-sends" />
                </div>
                <div>
                  <Label className="text-xs">Min lead (% points)</Label>
                  <Input type="number" min={1} value={f.auto_promote_min_lead} onChange={e => setF({...f, auto_promote_min_lead: Number(e.target.value)})} data-testid="threshold-min-lead" />
                </div>
              </div>
              <div className="text-xs text-neutral-500 mt-2">
                A variant auto-promotes when it hits <span className="font-mono">{f.auto_promote_min_sends}</span> sends and beats every other variant by ≥ <span className="font-mono">{f.auto_promote_min_lead}</span> reply-rate points.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-neutral-900 hover:bg-black" onClick={submit} data-testid="save-campaign-btn">Create campaign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
